import React, { useState, useEffect, useRef, useCallback } from "react";
import { API_BASE_URL } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminNotificationBell({ user }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const intervalRef = useRef(null);

  const rolePath = user?.role?.toLowerCase() || "admin";

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_BASE_URL}/${rolePath}/notifications`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("tvc_token")}` },
      });
      const data = await res.json();
      if (res.ok) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch {
      // silent
    }
  }, [user, rolePath]);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  const handleMarkRead = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/${rolePath}/notifications/${id}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("tvc_token")}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_BASE_URL}/${rolePath}/notifications/read-all`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("tvc_token")}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: 1 })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="AdminBellWrap">
      <button
        ref={bellRef}
        className="AdminBellBtn"
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && <span className="AdminBellBadge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {open && (
        <div ref={dropdownRef} className="AdminBellDropdown">
          <div className="AdminBellDropdownHeader">
            <span className="AdminBellDropdownTitle">Notifications</span>
            {unreadCount > 0 && (
              <button className="AdminBellMarkAllRead" onClick={handleMarkAllRead}>
                Mark all as read
              </button>
            )}
          </div>

          <div className="AdminBellDropdownBody">
            {notifications.length === 0 ? (
              <p className="AdminBellEmpty">No notifications yet.</p>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n.id}
                  className={`AdminBellItem ${!n.read ? "AdminBellItemUnread" : ""}`}
                  onClick={() => !n.read && handleMarkRead(n.id)}
                >
                  <div className="AdminBellItemTop">
                    {!n.read && <span className="AdminBellDot" />}
                    <span className="AdminBellItemTitle">{n.title}</span>
                    <span className="AdminBellItemTime">{timeAgo(n.createdAt)}</span>
                  </div>
                  <p className="AdminBellItemMsg">{n.message}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNotificationBell;